import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { normalizeAdminPath } from "@/lib/auth/routePermissions";

/** Match sidebar links: pathname-only items active for that path; `?tab=` links match full search. */
function navItemKey(url: string): string {
  const pathOnly = url.split(/[?#]/)[0] ?? url;
  const normalizedPath = normalizeAdminPath(pathOnly);
  if (!url.includes("?")) return normalizedPath;
  const qs = url.split("?")[1]?.split("#")[0] ?? "";
  return qs ? `${normalizedPath}?${qs}` : normalizedPath;
}

function currentNavKey(pathname: string, search: string): string {
  const normalizedPath = normalizeAdminPath(pathname);
  const qs = search.startsWith("?") ? search.slice(1) : search;
  return qs ? `${normalizedPath}?${qs}` : normalizedPath;
}
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface SidebarNavMenuItem {
  titleKey: string;
  url: string;
  icon: LucideIcon;
}

export interface SidebarNavMenuGroup {
  labelKey: string;
  icon: LucideIcon;
  items: SidebarNavMenuItem[];
}

type SidebarNavGroupProps = {
  group: SidebarNavMenuGroup;
  collapsed: boolean;
};

export function SidebarNavGroup({ group, collapsed }: SidebarNavGroupProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation("common");

  const here = currentNavKey(location.pathname, location.search);
  const isActive = (path: string) => here === navItemKey(path);
  const active = group.items.some((item) => isActive(item.url));
  const GroupIcon = group.icon;

  if (group.items.length === 1) {
    const item = group.items[0];
    const Icon = item.icon;

    if (collapsed) {
      return (
        <SidebarGroup className="py-0.5">
          <SidebarGroupContent>
            <SidebarMenu className="items-center">
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive(item.url)}
                  tooltip={t(item.titleKey)}
                  className="justify-center"
                >
                  <Link to={item.url}>
                    <Icon className="w-4 h-4 shrink-0" />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      );
    }

    return (
      <SidebarGroup className="py-0.5">
        <SidebarGroupLabel asChild>
          <Link
            to={item.url}
            className={cn(
              "flex h-8 w-full items-center gap-2 cursor-pointer rounded-md px-2 text-sm text-sidebar-foreground transition-colors",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              isActive(item.url) && "bg-secondary text-secondary-foreground font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{t(item.titleKey)}</span>
          </Link>
        </SidebarGroupLabel>
      </SidebarGroup>
    );
  }

  if (collapsed) {
    return (
      <SidebarGroup className="py-0.5">
        <SidebarGroupContent>
          <SidebarMenu className="items-center">
            <SidebarMenuItem>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <SidebarMenuButton
                    tooltip={t(group.labelKey)}
                    className={cn(
                      "justify-center",
                      active &&
                        "bg-secondary text-secondary-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-medium"
                    )}
                  >
                    <GroupIcon className="w-4 h-4 shrink-0" />
                  </SidebarMenuButton>
                </PopoverTrigger>
                <PopoverContent side="right" align="start" className="w-48 p-1.5 ml-1" onCloseAutoFocus={(e) => e.preventDefault()}>
                  <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(group.labelKey)}
                  </p>
                  <div className="mt-1 space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.titleKey}
                          to={item.url}
                          onClick={() => setOpen(false)}
                          className={cn(
                            "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                            isActive(item.url)
                              ? "bg-secondary text-secondary-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-medium"
                              : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          )}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span>{t(item.titleKey)}</span>
                        </Link>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <Collapsible defaultOpen={active} className="group/collapsible">
      <SidebarGroup className="py-0.5">
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger
            className={cn(
              "flex h-8 w-full items-center gap-2 cursor-pointer rounded-md px-2 text-sm text-sidebar-foreground transition-colors",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              active && "font-medium"
            )}
          >
            <GroupIcon className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{t(group.labelKey)}</span>
            <ChevronRight className="w-3.5 h-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent className="mt-1">
          <SidebarGroupContent className="ml-3 border-l border-sidebar-border pl-2">
            <SidebarMenu>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={t(item.titleKey)}
                      className="w-[90%] mx-auto text-xs"
                    >
                      <Link to={item.url}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span>{t(item.titleKey)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
